import React, { useCallback } from 'react';

import { Swap } from '@lyearn/icons';
import { DeprecatedIconButton, Divider, Popover, Typography } from '@lyearn/ui';

import MenuItems from './components/MenuItems';
import { swapStyle } from './style';
import { ListItemProps, SortMenuProps } from './types';
import useSort from './useSort';

const defaultSortButton = (disabled: boolean) => (
  <DeprecatedIconButton
    appearance="secondary"
    className="h-44 w-44 flex-shrink-0 bg-bg-primary"
    disabled={disabled}
    variant="outlined">
    <Swap className={swapStyle} />
  </DeprecatedIconButton>
);

const SortMenuMobile = <T extends ListItemProps>(props: SortMenuProps<T>) => {
  const { children, clearable, disabled = false, label, actions, onSort, defaultSortBy } = props;

  const { sortBy, setSortBy, sortActions } = useSort(actions, defaultSortBy);
  const onSortMenuClick = useCallback(
    (sort: string, item: T) => {
      const nextValue = clearable && sort === sortBy ? '' : sort;
      setSortBy(nextValue);
      onSort(nextValue, item);
    },
    [clearable, onSort, setSortBy, sortBy],
  );

  return (
    <Popover>
      {({ onClose }) => (
        <>
          <Popover.Button
            className="xs:me-8"
            classes={{ root: 'rounded-full justify-center items-center xs:h-32 xs:me-8' }}>
            {children || defaultSortButton(disabled)}
          </Popover.Button>
          <Popover.Content renderAs="bottom-sheet">
            <>
              {label ? (
                <Typography as="p" className="native:pt-8 px-24 pt-32 pb-20" variant="heading-s">
                  {label}
                </Typography>
              ) : null}
              <Divider color="border-interactive-secondary-hover" size={1} />
              <MenuItems
                actions={sortActions}
                className="ios:insert-padding-bottom-screen android:pb-18 px-8 pb-24 pt-10"
                closeBottomSheet={onClose}
                onMenuClick={onSortMenuClick}
              />
            </>
          </Popover.Content>
        </>
      )}
    </Popover>
  );
};

export default React.memo(SortMenuMobile) as typeof SortMenuMobile;
